import { Fragment } from 'react'
import { isEmpty } from 'lodash-es'

import { Badge } from 'Components/_theme'
import { Icon } from 'Components/Icon/Icon'
import { getTagColorStyles } from 'Components/Tagging/tagging.utils'

import {
  dashOrSpaceToUnderscore,
  getOrderDisplayName,
  sanitizeSearchHighlight,
} from 'Utilities'

import {
  ORDER_STATUS_BADGE_COLOR_MAP,
  ORDER_STATUS_BADGE_VARIANT_MAP,
  ORDER_STATUS_TRANSLATION_IDS,
} from 'Constants/orderStatus'
import { PORTAL_CONTENT_MODEL } from 'Constants/portalContentModel'

import {
  getReturnCasePortalStatusBadgeColor,
  getReturnCasePortalStatusBadgeVariant,
  getReturnCasePortalStatusTranslationId,
} from 'Portal/src/utilities/getReturnCasePortalStatus/getReturnCasePortalStatus.utils'

import type {
  GlobalSearchAutocompleteInlineHighlightProps,
  GlobalSearchAutocompleteItemProps,
} from './GlobalSearch.types'
import {
  getHighlightFieldLabel,
  isAutocompleteTagHighlight,
  parseOrderDisplay,
  parseReturnCaseDisplay,
  stripHtmlFromString,
} from './GlobalSearch.utils'
import { GlobalSearchOrgLabel } from './GlobalSearchOrgLabel'

const InlineHighlight = ({
  intl,
  highlightItemType,
  snippet,
}: GlobalSearchAutocompleteInlineHighlightProps) => {
  if (!highlightItemType || !snippet) {
    return null
  }

  return (
    <>
      {' '}
      ·{' '}
      {intl.formatMessage({
        defaultMessage: highlightItemType,
        id: highlightItemType,
      })}
      :{' '}
      <span
        dangerouslySetInnerHTML={{ __html: sanitizeSearchHighlight(snippet) }}
      />
    </>
  )
}

/** Normalize Typesense array highlights by their original tag index. */
const getSanitizedTagSnippetsByIndex = (
  tagHighlight:
    | GlobalSearchAutocompleteItemProps['suggestion']['highlights'][number]
    | undefined,
) => {
  const sanitizedTagSnippetsByIndex = new Map<number, string>()

  if (
    Array.isArray(tagHighlight?.indices) &&
    Array.isArray(tagHighlight.snippets)
  ) {
    tagHighlight.snippets.forEach((snippet, snippetIndex) => {
      const tagIndex = tagHighlight.indices?.[snippetIndex]

      if (typeof tagIndex === 'number' && typeof snippet === 'string') {
        sanitizedTagSnippetsByIndex.set(
          tagIndex,
          sanitizeSearchHighlight(snippet),
        )
      }
    })
  }

  return sanitizedTagSnippetsByIndex
}

const getTagPresentation = (
  highlights: GlobalSearchAutocompleteItemProps['suggestion']['highlights'],
  intl: GlobalSearchAutocompleteItemProps['intl'],
) => {
  const tagHighlight = highlights.find(isAutocompleteTagHighlight)
  const tagNamesWithIndices = Array.isArray(tagHighlight?.value)
    ? tagHighlight.value
        .map((name, index) => {
          const tag = tagHighlight.tags?.[index]

          return {
            color: tag?.name === name ? tag.color : undefined,
            index,
            name,
          }
        })
        .filter(tag => typeof tag.name === 'string' && tag.name.trim() !== '')
    : []
  const sanitizedTagSnippetsByIndex =
    getSanitizedTagSnippetsByIndex(tagHighlight)

  const hasTagHighlight = Boolean(
    tagHighlight && tagNamesWithIndices.length > 0,
  )
  const tagHighlightType = tagHighlight
    ? getHighlightFieldLabel(tagHighlight.field)
    : ''
  const translatedTagHighlightType = tagHighlightType
    ? intl.formatMessage({
        defaultMessage: tagHighlightType,
        id: tagHighlightType,
      })
    : ''
  const tagAccessibleLabel = hasTagHighlight
    ? `${translatedTagHighlightType}: ${tagNamesWithIndices
        .map(tag => tag.name)
        .join(' · ')}`
    : ''

  return {
    hasTagHighlight,
    sanitizedTagSnippetsByIndex,
    tagAccessibleLabel,
    tagHighlight,
    tagNamesWithIndices,
    translatedTagHighlightType,
  }
}

export const GlobalSearchAutocompleteItem = (
  props: GlobalSearchAutocompleteItemProps,
) => {
  const {
    suggestion,
    onSuggestionItemClickHandler,
    onDirectItemClickHandler,
    intl,
  } = props
  const {
    type,
    snippet,
    highlights,
    display,
    category,
    organisation_id: organisationId,
    organisation_name: organisationName,
  } = suggestion
  const { formatMessage } = intl
  const {
    hasTagHighlight,
    sanitizedTagSnippetsByIndex,
    tagAccessibleLabel,
    tagHighlight,
    tagNamesWithIndices,
    translatedTagHighlightType,
  } = getTagPresentation(highlights, intl)
  const getAccessibleLabel = (label: string) =>
    [stripHtmlFromString(label), tagAccessibleLabel].filter(Boolean).join(' · ')

  const renderTagHighlight = (showOrganisation = false) => {
    if (!hasTagHighlight || !tagHighlight) {
      return null
    }

    return (
      <div className="gs-autocomplete-direct-item__secondary-display">
        {showOrganisation && (
          <>
            <GlobalSearchOrgLabel
              organisationId={organisationId}
              organisationName={organisationName}
            />
            {Boolean(organisationId || organisationName) && (
              <span className="gs-autocomplete-org-label__separator">·</span>
            )}
          </>
        )}
        {translatedTagHighlightType}:{' '}
        {tagNamesWithIndices.map((tag, renderedIndex) => {
          const sanitizedSnippet = sanitizedTagSnippetsByIndex.get(tag.index)

          return (
            <Fragment key={`${tag.name}-${tag.index}`}>
              {renderedIndex > 0 && ' · '}
              <span className="d-inline-flex align-items-center">
                {tag.color && (
                  <span
                    aria-hidden
                    className="gs-autocomplete-tag-color d-inline-block flex-shrink-0 me-1 rounded-circle"
                    style={{
                      ...getTagColorStyles(tag.color),
                      borderStyle: 'solid',
                      borderWidth: 1,
                      height: 8,
                      width: 8,
                    }}
                  />
                )}
                {sanitizedSnippet ? (
                  <span
                    dangerouslySetInnerHTML={{ __html: sanitizedSnippet }}
                  />
                ) : (
                  <span>{tag.name}</span>
                )}
              </span>
            </Fragment>
          )
        })}
      </div>
    )
  }

  const renderSuggestionTemplate = () => {
    if (onSuggestionItemClickHandler) {
      const categoryIcon = PORTAL_CONTENT_MODEL[category].icon

      return (
        <button
          aria-label={stripHtmlFromString(snippet)}
          className="gs-autocomplete-suggestion-item"
          onClick={onSuggestionItemClickHandler}
          type="button"
        >
          <Icon className="px-1" icon={categoryIcon} />

          <div className="d-flex flex-column">
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeSearchHighlight(snippet),
              }}
              className="gs-autocomplete-suggestion-item__primary-display"
            />

            <GlobalSearchOrgLabel
              organisationId={organisationId}
              organisationName={organisationName}
            />
          </div>
        </button>
      )
    }

    return (
      <div
        dangerouslySetInnerHTML={{ __html: sanitizeSearchHighlight(snippet) }}
        aria-label={stripHtmlFromString(snippet)}
        className="gs-autocomplete-suggestion-item"
      />
    )
  }

  const renderDirectItemTemplate = () => {
    if (isEmpty(highlights)) {
      return renderSuggestionTemplate()
    }

    const highlight = highlights.find(
      highlightItem => !isAutocompleteTagHighlight(highlightItem),
    )
    const highlightItemType = highlight
      ? getHighlightFieldLabel(highlight.field)
      : null

    const categoryIcon = PORTAL_CONTENT_MODEL[category].icon

    const translatedHighlightType = highlightItemType
      ? intl.formatMessage({
          defaultMessage: highlightItemType,
          id: highlightItemType,
        })
      : null

    return (
      <button
        aria-label={getAccessibleLabel(snippet)}
        className="gs-autocomplete-direct-item d-flex align-items-center"
        onClick={onDirectItemClickHandler}
        type="button"
      >
        <Icon className="px-1" icon={categoryIcon} />

        <div className="d-flex flex-column w-100">
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeSearchHighlight(snippet),
            }}
            className="gs-autocomplete-direct-item__primary-display"
          />

          {highlight?.snippet && translatedHighlightType && (
            <div className="gs-autocomplete-direct-item__secondary-display">
              {translatedHighlightType}:{' '}
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeSearchHighlight(highlight.snippet),
                }}
              />
              <GlobalSearchOrgLabel
                inline
                organisationId={organisationId}
                organisationName={organisationName}
              />
            </div>
          )}

          {renderTagHighlight(!highlight?.snippet)}
        </div>
      </button>
    )
  }

  const renderOrderItemTemplate = () => {
    const parsedDisplay = parseOrderDisplay(display)
    const displayName = getOrderDisplayName(
      parsedDisplay.type,
      parsedDisplay.name,
    )
    const formattedStatus = dashOrSpaceToUnderscore(parsedDisplay.status)

    const renderDisplayName = () => {
      if (typeof displayName === 'string') {
        return formatMessage({ id: displayName })
      }

      return `${formatMessage({ id: displayName.type })}: ${displayName.name}`
    }

    const highlight = highlights.find(
      highlightItem => !isAutocompleteTagHighlight(highlightItem),
    )
    const highlightItemType = highlight
      ? getHighlightFieldLabel(highlight.field)
      : null

    return (
      <button
        aria-label={getAccessibleLabel(parsedDisplay.highlight)}
        className="gs-autocomplete-direct-item d-flex align-items-center"
        onClick={onDirectItemClickHandler}
        type="button"
      >
        <div className="d-flex flex-column w-100">
          <div className="d-flex align-items-center justify-content-between">
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeSearchHighlight(renderDisplayName()),
              }}
              className="gs-autocomplete-direct-item__primary-display"
            />

            <Badge
              className="me-1"
              color={ORDER_STATUS_BADGE_COLOR_MAP[formattedStatus]}
              variant={ORDER_STATUS_BADGE_VARIANT_MAP[formattedStatus]}
            >
              {formatMessage({
                id: ORDER_STATUS_TRANSLATION_IDS[formattedStatus],
              })}
            </Badge>
          </div>

          <div className="gs-autocomplete-direct-item__secondary-display">
            {formatMessage({ id: 'Request_id' })}:{' '}
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizeSearchHighlight(parsedDisplay.highlight),
              }}
            />
            <InlineHighlight
              highlightItemType={highlightItemType}
              intl={intl}
              snippet={highlight?.snippet}
            />
            <GlobalSearchOrgLabel
              inline
              organisationId={organisationId}
              organisationName={organisationName}
            />
          </div>

          {renderTagHighlight()}
        </div>
      </button>
    )
  }

  const renderReturnCaseItemTemplate = () => {
    const parsedDisplay = parseReturnCaseDisplay(display)
    const displayName = `${formatMessage({ id: 'Returns' })}: ${
      parsedDisplay.name
    }`

    const [highlight] = highlights ?? []
    const highlightItemType = highlight
      ? getHighlightFieldLabel(highlight.field)
      : null

    return (
      <button
        aria-label={stripHtmlFromString(parsedDisplay.highlight)}
        className="gs-autocomplete-direct-item d-flex align-items-center"
        onClick={onDirectItemClickHandler}
        type="button"
      >
        <div className="d-flex flex-column w-100">
          <div className="d-flex align-items-center justify-content-between">
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeSearchHighlight(displayName),
              }}
              className="gs-autocomplete-direct-item__primary-display"
            />

            <Badge
              className="me-1"
              color={getReturnCasePortalStatusBadgeColor(
                parsedDisplay.portalStatus,
              )}
              variant={getReturnCasePortalStatusBadgeVariant(
                parsedDisplay.portalStatus,
              )}
            >
              {formatMessage({
                id: getReturnCasePortalStatusTranslationId(
                  parsedDisplay.portalStatus,
                ),
              })}
            </Badge>
          </div>

          <div className="gs-autocomplete-direct-item__secondary-display">
            {formatMessage({ id: 'Return_id' })}:{' '}
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizeSearchHighlight(parsedDisplay.highlight),
              }}
            />
            <InlineHighlight
              highlightItemType={highlightItemType}
              intl={intl}
              snippet={highlight?.snippet}
            />
            <GlobalSearchOrgLabel
              inline
              organisationId={organisationId}
              organisationName={organisationName}
            />
          </div>
        </div>
      </button>
    )
  }

  const renderTemplate = () => {
    if (type === 'direct' && category === 'return_cases') {
      return renderReturnCaseItemTemplate()
    }

    if (type === 'direct' && category === 'caseitems') {
      return renderOrderItemTemplate()
    }

    if (type === 'direct' || hasTagHighlight) {
      return renderDirectItemTemplate()
    }

    return renderSuggestionTemplate()
  }

  return renderTemplate()
}
